/**
 * RelayPool — resilient multi-relay transport (SPEC §10, NET-REL-001..006).
 *
 * Replaces the old RelayClient, whose reconnect loop tore down sockets that were
 * still handshaking, never re-sent subscriptions after a reconnect, had no
 * catch-up window, and published to relay sets the recipient might not read.
 *
 *  - One state machine per relay: idle → connecting → ready → (closed) → backoff.
 *    In-flight handshakes are never interrupted; failures back off exponentially
 *    with jitter (1 s … 5 min) and a relay that was stable reconnects at once.
 *  - Inbox subscriptions are persistent: re-sent on every (re)connect with a
 *    per-relay `since` watermark (persisted), so offline catch-up is exact.
 *    Catch-up is paginated backwards with `until` (relays return only the newest
 *    `limit` events), delivered OLDEST-first once complete, and only then advances
 *    the watermark — a long offline period can no longer skip older messages, and
 *    stale messages are no longer processed after newer ones (2026-10 review).
 *  - Standard NIP-17 inbox routing: events carry `p` = recipient transport key;
 *    the pool answers NIP-42 AUTH challenges with that key (through a signer —
 *    the secret stays in the crypto core), so gift-wrap-aware relays that gate
 *    kind 1059 (e.g. damus) serve the recipient. Legacy `w` hints still work.
 *  - Rendezvous: sender and recipient independently rank relays with
 *    highest-random-weight hashing on the recipient's routing key and use the
 *    first reachable ones — no directory needed for their relay sets to meet.
 *  - Liveness pings detect silently dead sockets; `nudge()` retries on
 *    online / foreground.
 */

export type PoolTier = "private" | "community";

export interface PoolRelay {
  url: string;
  tier: PoolTier;
}

/** Legacy relay-dev challenge–response (Ed25519 messaging key). */
export type DevRelayAuthenticator = {
  messagingPublicKeyHex: string;
  sign: (message: Uint8Array) => Uint8Array;
};

/** NIP-42 signer: BIP-340 over the 32-byte event id (transport key, in-core). */
export type Nip42Signer = {
  pubkeyHex: string;
  signId: (id32: Uint8Array) => Uint8Array;
};

export interface InboxSpec {
  /** Rendezvous key (our 16-hex messaging hint) — senders rank relays with it. */
  routingKey: string;
  /** Our transport x-only pubkeys (64 hex) — `#p` routing (NIP-17 style). */
  p: string[];
  /** Legacy 16-hex routing hints — `#w`. */
  w: string[];
}

export type RelayState = "idle" | "connecting" | "ready" | "backoff";

export interface PoolRelayStatus {
  url: string;
  tier: PoolTier;
  state: RelayState;
  connected: boolean;
  authed: boolean;
  latencyMs: number | null;
  failures: number;
  nextAttemptAt: number | null;
  publishOk: number;
  publishFail: number;
  eventsIn: number;
  lastEventAt: number | null;
  lastError: string | null;
}

export interface PoolPublishResult {
  acks: number;
  privateAcks: number;
  communityAcks: number;
  results: { url: string; ok: boolean; tier: PoolTier; error?: string }[];
}

type WsLike = {
  readyState: number;
  send(data: string): void;
  close(code?: number, reason?: string): void;
  onopen: ((ev: unknown) => void) | null;
  onclose: ((ev: unknown) => void) | null;
  onerror: ((ev: unknown) => void) | null;
  onmessage: ((ev: { data: unknown }) => void) | null;
};

export type WebSocketFactory = (url: string) => WsLike;

export interface SinceStore {
  load(): Record<string, number>;
  save(map: Record<string, number>): void;
}

interface Conn {
  url: string;
  key: string;
  tier: PoolTier;
  state: RelayState;
  ws: WsLike | null;
  failures: number;
  nextAttemptAt: number;
  connectStartedAt: number;
  readyAt: number;
  latencyMs: number | null;
  authed: boolean;
  lastInboundAt: number;
  pingSentAt: number | null;
  pingSubId: string | null;
  pendingOk: Map<string, (ok: boolean, msg: string) => void>;
  /** EVENT frames by id while awaiting OK (re-sent after an auth-required refusal). */
  frames: Map<string, string>;
  /** Frames to send once ready / authenticated (queued publishes). */
  queue: string[];
  publishOk: number;
  publishFail: number;
  eventsIn: number;
  lastEventAt: number | null;
  lastError: string | null;
  authRequired: boolean;
  /** Challenge received while no signer was installed (answered once one is). */
  pendingChallenge: unknown;
  /** Last reconnect performed only to obtain a fresh AUTH challenge. */
  reauthAt: number;
  /** Inbox catch-up in progress, by base subscription id. */
  catchup: Map<string, Catchup>;
}

/** Paginated offline catch-up for one inbox subscription. */
interface Catchup {
  filterKey: "#p" | "#w";
  values: string[];
  since: number;
  page: number;
  /** Subscription id of the page being read (base id for page 0). */
  pageSubId: string;
  baseEosed: boolean;
  pageCount: number;
  oldest: number;
  newest: number;
  buffer: Map<string, { frame: unknown[]; createdAt: number }>;
  startedAt: number;
}

const OPEN = 1;
const HANDSHAKE_TIMEOUT_MS = 8_000;
const PING_EVERY_MS = 45_000;
const PING_TIMEOUT_MS = 12_000;
const BACKOFF_BASE_MS = 1_000;
const BACKOFF_MAX_MS = 5 * 60_000;
const STABLE_AFTER_MS = 60_000;
const CATCHUP_MARGIN_S = 15 * 60;
const CATCHUP_MAX_S = 30 * 24 * 3600;
const CATCHUP_PAGE = 500;
const CATCHUP_MAX_PAGES = 20;
const CATCHUP_TIMEOUT_MS = 20_000;
const TICK_MS = 1_500;
const MAX_QUEUED_FRAMES = 50;
const PING_FILTER = { ids: ["0".repeat(64)], limit: 1 };

export function normalizeRelayUrl(url: string): string {
  return url.trim().toLowerCase().replace(/\/+$/, "");
}

/** cyrb53 — small, fast, well-distributed 53-bit string hash (deterministic across clients). */
function hash53(str: string, seed = 0): number {
  let h1 = 0xdeadbeef ^ seed;
  let h2 = 0x41c6ce57 ^ seed;
  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return 4294967296 * (2097151 & h2) + (h1 >>> 0);
}

/**
 * Highest-random-weight ordering of `urls` for a routing key: every client that
 * knows the same relay list computes the same order for the same recipient.
 */
export function rendezvousOrder(routingKey: string, urls: string[]): string[] {
  const k = routingKey.toLowerCase();
  return [...urls]
    .map((u) => ({ u, w: hash53(`${k}|${normalizeRelayUrl(u)}`) }))
    .sort((a, b) => b.w - a.w || (a.u < b.u ? -1 : 1))
    .map((x) => x.u);
}

function bytesToHex(b: Uint8Array): string {
  return Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
}

function hexToBytes(hex: string): Uint8Array {
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  return out;
}

async function sha256Hex(text: string): Promise<string> {
  const data = new TextEncoder().encode(text);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return bytesToHex(new Uint8Array(digest));
}

export class RelayPool {
  private conns = new Map<string, Conn>();
  private handlers = new Set<(ev: unknown, url: string, tier: PoolTier) => void>();
  private inbox: InboxSpec | null = null;
  private devAuth: DevRelayAuthenticator | null = null;
  private nip42: Nip42Signer | null = null;
  private since: Record<string, number>;
  private sinceDirty = false;
  private lastSinceSave = 0;
  private timer: ReturnType<typeof setInterval> | null = null;
  private seq = 0;
  private readonly makeSocket: WebSocketFactory;
  private readonly now: () => number;
  private readonly sinceStore: SinceStore | null;
  private readonly maxConnections: number;
  private readonly autoStart: boolean;

  constructor(opts: {
    socketFactory?: WebSocketFactory;
    now?: () => number;
    sinceStore?: SinceStore | null;
    maxConnections?: number;
    autoStart?: boolean;
  } = {}) {
    this.makeSocket =
      opts.socketFactory ?? ((url: string) => new WebSocket(url) as unknown as WsLike);
    this.now = opts.now ?? (() => Date.now());
    this.sinceStore = opts.sinceStore ?? null;
    this.since = this.sinceStore?.load() ?? {};
    this.maxConnections = opts.maxConnections ?? 14;
    this.autoStart = opts.autoStart !== false;
    if (this.autoStart) this.start();
  }

  start(): void {
    if (this.timer) return;
    this.timer = setInterval(() => this.tick(), TICK_MS);
  }

  // -------------------------------------------------------------------------
  // Configuration
  // -------------------------------------------------------------------------

  setAuth(opts: { dev?: DevRelayAuthenticator | null; nip42?: Nip42Signer | null }): void {
    if (opts.dev !== undefined) this.devAuth = opts.dev;
    if (opts.nip42 !== undefined) this.nip42 = opts.nip42;
    // Answer challenges that arrived before a signer existed (app locked at connect).
    for (const c of this.conns.values()) {
      if (c.state === "ready" && !c.authed && c.pendingChallenge != null) {
        const ch = c.pendingChallenge;
        c.pendingChallenge = null;
        void this.answerAuth(c, ch);
      }
    }
  }

  /** Desired relay set. Removed relays are closed; new ones connect on the next tick. */
  setRelays(relays: PoolRelay[]): void {
    // (Re)configuring a closed pool revives it (React StrictMode remounts).
    if (this.autoStart) this.start();
    const wanted = new Map<string, PoolRelay>();
    for (const r of relays) {
      if (!/^wss?:\/\//i.test(r.url)) continue;
      const key = normalizeRelayUrl(r.url);
      // Private wins if a URL appears in both tiers.
      if (!wanted.has(key) || r.tier === "private") wanted.set(key, r);
    }
    for (const [key, c] of this.conns) {
      if (!wanted.has(key)) {
        this.teardown(c, "removed");
        this.conns.delete(key);
      }
    }
    for (const [key, r] of wanted) {
      const existing = this.conns.get(key);
      if (existing) {
        existing.tier = r.tier;
        continue;
      }
      this.conns.set(key, this.blank(r.url, key, r.tier));
    }
    this.tick();
  }

  /** Our inbox routing keys; subscriptions follow every (re)connect. */
  setInbox(spec: InboxSpec | null): void {
    this.inbox =
      spec && (spec.p.length || spec.w.length)
        ? {
            routingKey: spec.routingKey.toLowerCase(),
            p: spec.p.map((x) => x.toLowerCase()),
            w: spec.w.map((x) => x.toLowerCase()),
          }
        : null;
    for (const c of this.conns.values()) if (c.state === "ready") this.sendInboxReqs(c);
  }

  onEvent(handler: (ev: unknown, url: string, tier: PoolTier) => void): () => void {
    this.handlers.add(handler);
    return () => this.handlers.delete(handler);
  }

  /** Retry every backed-off relay now (network came back / app foregrounded). */
  nudge(): void {
    const t = this.now();
    for (const c of this.conns.values()) {
      if (c.state === "backoff") {
        c.state = "idle";
        c.nextAttemptAt = t;
      } else if (c.state === "ready") {
        this.ping(c);
      }
    }
    this.tick();
  }

  close(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    for (const c of this.conns.values()) this.teardown(c, "pool closed");
    this.conns.clear();
    this.flushSince(true);
  }

  // -------------------------------------------------------------------------
  // Introspection
  // -------------------------------------------------------------------------

  statuses(): PoolRelayStatus[] {
    return [...this.conns.values()].map((c) => ({
      url: c.url,
      tier: c.tier,
      state: c.state,
      connected: c.state === "ready",
      authed: c.authed,
      latencyMs: c.latencyMs,
      failures: c.failures,
      nextAttemptAt: c.state === "backoff" ? c.nextAttemptAt : null,
      publishOk: c.publishOk,
      publishFail: c.publishFail,
      eventsIn: c.eventsIn,
      lastEventAt: c.lastEventAt,
      lastError: c.lastError,
    }));
  }

  readyCount(tier?: PoolTier): number {
    let n = 0;
    for (const c of this.conns.values()) if (c.state === "ready" && (!tier || c.tier === tier)) n += 1;
    return n;
  }

  isReady(url: string): boolean {
    return this.conns.get(normalizeRelayUrl(url))?.state === "ready";
  }

  /** First `k` reachable relays in rendezvous order for `routingKey`. */
  rendezvous(routingKey: string, k: number, tier?: PoolTier): string[] {
    const candidates = [...this.conns.values()].filter((c) => !tier || c.tier === tier);
    const order = rendezvousOrder(
      routingKey,
      candidates.map((c) => c.url),
    );
    const byUrl = new Map(candidates.map((c) => [c.url, c]));
    const picked: string[] = [];
    // Pass 1: relays that are not in a long failure streak.
    for (const u of order) {
      const c = byUrl.get(u)!;
      if (c.failures >= 3 || this.writeRejectRate(c) > 0.8) continue;
      picked.push(u);
      if (picked.length >= k) return picked;
    }
    // Pass 2: fill with anything left (everything may be struggling).
    for (const u of order) {
      if (!picked.includes(u)) picked.push(u);
      if (picked.length >= k) break;
    }
    return picked;
  }

  // -------------------------------------------------------------------------
  // Publish
  // -------------------------------------------------------------------------

  /**
   * Publish to the recipient's rendezvous relays plus our healthiest ready
   * relays; resolves when `targetAcks` OKs arrived, every attempt settled, or
   * `timeoutMs` elapsed. Expands to further relays when too few acked.
   */
  async publish(
    event: Record<string, unknown> & { id: string },
    opts: { routingKey: string; targetAcks?: number; timeoutMs?: number; rendezvousK?: number; extra?: number } ,
  ): Promise<PoolPublishResult> {
    const targetAcks = opts.targetAcks ?? 3;
    const timeoutMs = opts.timeoutMs ?? 5_000;
    const frame = JSON.stringify(["EVENT", event]);
    const tried = new Set<string>();
    const results: PoolPublishResult["results"] = [];

    const firstWave = this.pickTargets(opts.routingKey, opts.rendezvousK ?? 4, opts.extra ?? 2);
    await this.sendWave(firstWave, event.id, frame, timeoutMs, targetAcks, tried, results);
    let acks = results.filter((r) => r.ok).length;
    if (acks < 2) {
      // NET-REL-003 expansion: continue down the rendezvous order.
      const more = rendezvousOrder(opts.routingKey, [...this.conns.values()].map((c) => c.url))
        .filter((u) => !tried.has(u))
        .slice(0, 4);
      if (more.length) await this.sendWave(more, event.id, frame, timeoutMs, 2 - acks, tried, results);
      acks = results.filter((r) => r.ok).length;
    }
    return {
      acks,
      privateAcks: results.filter((r) => r.ok && r.tier === "private").length,
      communityAcks: results.filter((r) => r.ok && r.tier === "community").length,
      results,
    };
  }

  private pickTargets(routingKey: string, k: number, extra: number): string[] {
    const out = new Set<string>(this.rendezvous(routingKey, k));
    // Our own private relays are always included (operated infrastructure).
    for (const c of this.conns.values()) if (c.tier === "private" && c.failures < 5) out.add(c.url);
    const ready = [...this.conns.values()]
      .filter((c) => c.state === "ready" && !out.has(c.url))
      .sort((a, b) => this.health(b) - this.health(a));
    for (const c of ready.slice(0, extra)) out.add(c.url);
    return [...out];
  }

  private sendWave(
    urls: string[],
    id: string,
    frame: string,
    timeoutMs: number,
    wantAcks: number,
    tried: Set<string>,
    results: PoolPublishResult["results"],
  ): Promise<void> {
    return new Promise((resolve) => {
      let pending = 0;
      let acks = 0;
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        clearTimeout(timer);
        resolve();
      };
      const timer = setTimeout(() => {
        for (const u of urls) {
          const c = this.conns.get(normalizeRelayUrl(u));
          const cb = c?.pendingOk.get(id);
          if (cb) cb(false, "ack-timeout");
        }
        finish();
      }, timeoutMs);
      for (const u of urls) {
        const c = this.conns.get(normalizeRelayUrl(u));
        if (!c) continue;
        tried.add(c.url);
        pending += 1;
        const settle = (ok: boolean, msg: string) => {
          if (!c.pendingOk.has(id)) return;
          c.pendingOk.delete(id);
          c.frames.delete(id);
          if (ok) c.publishOk += 1;
          else if (msg !== "ack-timeout" && !msg.startsWith("duplicate")) c.publishFail += 1;
          // Relays answer "duplicate:" for events they already hold — that is an ack.
          const accepted = ok || msg.startsWith("duplicate");
          results.push({ url: c.url, ok: accepted, tier: c.tier, error: accepted ? undefined : msg });
          if (accepted) acks += 1;
          pending -= 1;
          if (acks >= wantAcks || pending === 0) finish();
        };
        c.pendingOk.set(id, settle);
        c.frames.set(id, frame);
        if (c.state === "ready" && c.ws?.readyState === OPEN) {
          try {
            c.ws.send(frame);
          } catch (e) {
            settle(false, String(e));
          }
        } else {
          this.enqueue(c, frame);
          if (c.state === "backoff") {
            // A queued publish is worth an early retry.
            c.state = "idle";
            c.nextAttemptAt = this.now();
          }
          this.connect(c);
        }
      }
      if (pending === 0) finish();
    });
  }

  // -------------------------------------------------------------------------
  // Connection lifecycle
  // -------------------------------------------------------------------------

  private blank(url: string, key: string, tier: PoolTier): Conn {
    return {
      url,
      key,
      tier,
      state: "idle",
      ws: null,
      failures: 0,
      nextAttemptAt: 0,
      connectStartedAt: 0,
      readyAt: 0,
      latencyMs: null,
      authed: false,
      lastInboundAt: 0,
      pingSentAt: null,
      pingSubId: null,
      pendingOk: new Map(),
      frames: new Map(),
      queue: [],
      publishOk: 0,
      publishFail: 0,
      eventsIn: 0,
      lastEventAt: null,
      lastError: null,
      authRequired: false,
      pendingChallenge: null,
      catchup: new Map(),
      reauthAt: 0,
    };
  }

  private health(c: Conn): number {
    const lat = c.latencyMs ?? 1500;
    return 2 - this.writeRejectRate(c) * 2 - Math.min(lat, 3000) / 3000 - c.failures * 0.3 + (c.tier === "private" ? 0.5 : 0);
  }

  private writeRejectRate(c: Conn): number {
    const total = c.publishOk + c.publishFail;
    return total < 3 ? 0 : c.publishFail / total;
  }

  private tick(): void {
    const t = this.now();
    let active = 0;
    for (const c of this.conns.values()) if (c.state === "ready" || c.state === "connecting") active += 1;
    const inboxPriority = new Set(this.inbox ? this.rendezvous(this.inbox.routingKey, 5) : []);
    for (const c of this.conns.values()) {
      if (c.state === "backoff" && t >= c.nextAttemptAt) c.state = "idle";
      if (c.state === "idle") {
        const priority = c.tier === "private" || inboxPriority.has(c.url) || c.queue.length > 0;
        if (priority || active < this.maxConnections) {
          this.connect(c);
          active += 1;
        }
      } else if (c.state === "connecting") {
        if (t - c.connectStartedAt > HANDSHAKE_TIMEOUT_MS) this.fail(c, "handshake timeout");
      } else if (c.state === "ready") {
        // A relay that never finishes its catch-up (no EOSE) still delivers.
        for (const [base, cu] of [...c.catchup]) {
          if (t - cu.startedAt > CATCHUP_TIMEOUT_MS) this.finishCatchup(c, base, cu);
        }
        if (c.pingSentAt !== null) {
          // Any frame after the ping proves the socket is alive.
          if (c.lastInboundAt >= c.pingSentAt) {
            c.pingSentAt = null;
            c.pingSubId = null;
          } else if (t - c.pingSentAt > PING_TIMEOUT_MS) {
            this.fail(c, "ping timeout");
          }
        } else if (t - Math.max(c.lastInboundAt, c.readyAt) > PING_EVERY_MS) {
          this.ping(c);
        }
        if (
          c.authRequired &&
          !c.authed &&
          c.pendingChallenge == null &&
          (this.devAuth || this.nip42) &&
          t - c.readyAt > 4_000 &&
          t - c.reauthAt > 5 * 60_000
        ) {
          // Refused for lack of auth and no challenge to answer: a fresh connection gets one.
          c.reauthAt = t;
          this.teardown(c, "re-auth");
          c.state = "idle";
          c.failures = 0;
          c.authRequired = false;
        }
      }
    }
    this.flushSince(false);
  }

  private connect(c: Conn): void {
    if (c.state === "connecting" || c.state === "ready") return;
    c.state = "connecting";
    c.connectStartedAt = this.now();
    c.authed = false;
    let ws: WsLike;
    try {
      ws = this.makeSocket(c.url);
    } catch (e) {
      this.fail(c, String(e));
      return;
    }
    c.ws = ws;
    ws.onopen = () => {
      if (c.ws !== ws) return;
      c.state = "ready";
      c.readyAt = this.now();
      c.lastInboundAt = c.readyAt;
      c.latencyMs = c.readyAt - c.connectStartedAt;
      c.lastError = null;
      this.sendInboxReqs(c);
      const q = c.queue.splice(0, c.queue.length);
      for (const f of q) ws.send(f);
    };
    ws.onmessage = (m) => {
      if (c.ws !== ws) return;
      c.lastInboundAt = this.now();
      let data: unknown;
      try {
        data = JSON.parse(String(m.data));
      } catch {
        return;
      }
      if (Array.isArray(data)) this.onFrame(c, data);
    };
    ws.onerror = () => {
      if (c.ws === ws) c.lastError = "socket error";
    };
    ws.onclose = () => {
      if (c.ws !== ws) return;
      this.fail(c, c.lastError ?? "closed");
    };
  }

  private fail(c: Conn, reason: string): void {
    const wasStable = c.state === "ready" && this.now() - c.readyAt > STABLE_AFTER_MS;
    this.teardown(c, reason);
    c.failures = wasStable ? 1 : c.failures + 1;
    const exp = Math.min(BACKOFF_MAX_MS, BACKOFF_BASE_MS * 2 ** Math.min(c.failures - 1, 12));
    const jitter = 0.5 + Math.random();
    c.nextAttemptAt = this.now() + (wasStable ? 500 : exp * jitter);
    c.state = "backoff";
    c.lastError = reason;
  }

  private teardown(c: Conn, reason: string): void {
    const ws = c.ws;
    c.ws = null;
    c.authed = false;
    c.pingSentAt = null;
    c.pingSubId = null;
    if (ws) {
      ws.onopen = ws.onclose = ws.onerror = null;
      ws.onmessage = null;
      try {
        ws.close();
      } catch {
        /* ignore */
      }
    }
    for (const cb of [...c.pendingOk.values()]) cb(false, `disconnected: ${reason}`);
    c.pendingOk.clear();
    c.frames.clear();
    // Unfinished catch-up is dropped WITHOUT moving the watermark: the next
    // connection fetches the same window again (handled ids are skipped upstream).
    c.catchup.clear();
    if (c.state !== "backoff") c.state = "idle";
  }

  private ping(c: Conn): void {
    if (!c.ws || c.state !== "ready") return;
    const subId = `ping-${++this.seq}`;
    c.pingSubId = subId;
    c.pingSentAt = this.now();
    try {
      // Matches nothing on any relay: the EOSE (or any reply) is the liveness proof.
      c.ws.send(JSON.stringify(["REQ", subId, PING_FILTER]));
    } catch {
      this.fail(c, "ping send failed");
    }
  }

  private sinceFor(c: Conn): number {
    const nowS = Math.floor(this.now() / 1000);
    const mark = this.since[c.key];
    const floor = nowS - CATCHUP_MAX_S;
    return typeof mark === "number" ? Math.max(floor, mark - CATCHUP_MARGIN_S) : floor;
  }

  private sendInboxReqs(c: Conn): void {
    if (!this.inbox || !c.ws || c.state !== "ready") return;
    const since = this.sinceFor(c);
    c.catchup.clear();
    const subs: [string, "#p" | "#w", string[]][] = [];
    if (this.inbox.p.length) subs.push(["inbox-p", "#p", this.inbox.p]);
    if (this.inbox.w.length) subs.push(["inbox-w", "#w", this.inbox.w]);
    try {
      for (const [base, filterKey, values] of subs) {
        c.catchup.set(base, {
          filterKey,
          values,
          since,
          page: 0,
          pageSubId: base,
          baseEosed: false,
          pageCount: 0,
          oldest: Number.POSITIVE_INFINITY,
          newest: 0,
          buffer: new Map(),
          startedAt: this.now(),
        });
        c.ws.send(JSON.stringify(["REQ", base, { kinds: [1059], [filterKey]: values, since, limit: CATCHUP_PAGE }]));
      }
    } catch {
      this.fail(c, "subscribe failed");
    }
  }

  /** Watermark only ever moves to plausible times (never trust future stamps). */
  private advanceSince(c: Conn, createdAt: number): void {
    const prev = this.since[c.key] ?? 0;
    const capped = Math.min(createdAt, Math.floor(this.now() / 1000) + 300);
    if (capped > prev) {
      this.since[c.key] = capped;
      this.sinceDirty = true;
    }
  }

  /** EOSE (or refusal / timeout) for the page being read. */
  private catchupPageDone(c: Conn, base: string, cu: Catchup): void {
    if (cu.pageSubId !== base) {
      try {
        c.ws?.send(JSON.stringify(["CLOSE", cu.pageSubId]));
      } catch {
        /* ignore */
      }
    }
    cu.baseEosed = true;
    const full = cu.pageCount >= CATCHUP_PAGE;
    if (full && cu.page + 1 < CATCHUP_MAX_PAGES && Number.isFinite(cu.oldest) && c.ws) {
      // The relay only returned the newest page: read further back.
      cu.page += 1;
      cu.pageCount = 0;
      cu.pageSubId = `${base}~${cu.page}`;
      try {
        c.ws.send(
          JSON.stringify([
            "REQ",
            cu.pageSubId,
            { kinds: [1059], [cu.filterKey]: cu.values, since: cu.since, until: cu.oldest, limit: CATCHUP_PAGE },
          ]),
        );
        return;
      } catch {
        /* fall through: deliver what we have */
      }
    }
    this.finishCatchup(c, base, cu);
  }

  /** Deliver the whole catch-up oldest-first, then move the watermark. */
  private finishCatchup(c: Conn, base: string, cu: Catchup): void {
    c.catchup.delete(base);
    const ordered = [...cu.buffer.values()].sort((a, b) => a.createdAt - b.createdAt);
    for (const { frame } of ordered) for (const h of this.handlers) h(frame, c.url, c.tier);
    if (cu.newest > 0) this.advanceSince(c, cu.newest);
  }

  private onFrame(c: Conn, data: unknown[]): void {
    const type = data[0];
    if (type === "EVENT") {
      const sub = String(data[1] ?? "");
      if (!sub.startsWith("inbox-")) return;
      const ev = data[2] as { id?: unknown; created_at?: unknown } | undefined;
      c.eventsIn += 1;
      c.lastEventAt = this.now();
      const createdAt = ev && typeof ev.created_at === "number" ? ev.created_at : 0;
      const base = sub.split("~")[0]!;
      const cu = c.catchup.get(base);
      if (cu && (sub !== base || !cu.baseEosed)) {
        // Stored event of the catch-up window: buffer, deliver sorted at the end.
        if (sub === cu.pageSubId) cu.pageCount += 1;
        if (createdAt) {
          cu.oldest = Math.min(cu.oldest, createdAt);
          cu.newest = Math.max(cu.newest, createdAt);
        }
        const id = typeof ev?.id === "string" ? ev.id : `${sub}:${cu.buffer.size}`;
        if (!cu.buffer.has(id)) cu.buffer.set(id, { frame: data, createdAt });
        return;
      }
      if (cu) {
        // Live event while older pages are still being read: deliver now, but the
        // watermark waits for the catch-up to complete.
        if (createdAt) cu.newest = Math.max(cu.newest, createdAt);
      } else if (createdAt) {
        this.advanceSince(c, createdAt);
      }
      for (const h of this.handlers) h(data, c.url, c.tier);
      return;
    }
    if (type === "EOSE") {
      const sub = String(data[1] ?? "");
      const base = sub.split("~")[0]!;
      const cu = c.catchup.get(base);
      if (cu && sub === cu.pageSubId) {
        this.catchupPageDone(c, base, cu);
        return;
      }
      if (sub === c.pingSubId) {
        c.pingSentAt = null;
        c.pingSubId = null;
        try {
          c.ws?.send(JSON.stringify(["CLOSE", sub]));
        } catch {
          /* ignore */
        }
      }
      return;
    }
    if (type === "OK") {
      const id = String(data[1] ?? "");
      const msg = String(data[3] ?? "");
      const frame = c.frames.get(id);
      if (data[2] !== true && frame && /^auth-required/i.test(msg) && !c.authed && (this.nip42 || this.devAuth)) {
        // Refused before we authenticated: resend once the AUTH handshake completes.
        c.authRequired = true;
        this.enqueue(c, frame);
        return;
      }
      const cb = c.pendingOk.get(id);
      if (cb) cb(data[2] === true, msg);
      if (id === "auth") {
        // relay-dev challenge–response acknowledgement.
        if (data[2] === true) this.onAuthed(c);
        else c.lastError = `auth rejected: ${String(data[3] ?? "")}`;
      }
      return;
    }
    if (type === "CLOSED") {
      const msg = String(data[2] ?? "");
      if (String(data[1] ?? "") === c.pingSubId) {
        c.pingSentAt = null;
        c.pingSubId = null;
      }
      const sub = String(data[1] ?? "");
      if (/^auth-required/i.test(msg)) c.authRequired = true;
      else if (sub.startsWith("inbox-")) c.lastError = `subscription refused: ${msg.slice(0, 80)}`;
      const base = sub.split("~")[0]!;
      const cu = c.catchup.get(base);
      if (cu && sub === cu.pageSubId) this.finishCatchup(c, base, cu); // deliver what we have
      return;
    }
    if (type === "NOTICE") {
      const msg = String(data[1] ?? "");
      if (/auth-required/i.test(msg)) c.authRequired = true;
      return;
    }
    if (type === "AUTH") {
      void this.answerAuth(c, data[1]);
      return;
    }
  }

  private async answerAuth(c: Conn, challenge: unknown): Promise<void> {
    const ws = c.ws;
    if (!ws) return;
    const canNip42 = typeof challenge === "string" && this.nip42;
    const canDev = !!challenge && typeof challenge === "object" && this.devAuth;
    if (!canNip42 && !canDev) {
      c.pendingChallenge = challenge;
      return;
    }
    if (typeof challenge === "string" && this.nip42) {
      // NIP-42: kind-22242 event signed with the inbox (transport) key.
      const unsigned = {
        pubkey: this.nip42.pubkeyHex.toLowerCase(),
        created_at: Math.floor(this.now() / 1000),
        kind: 22242,
        tags: [
          ["relay", c.url],
          ["challenge", challenge],
        ],
        content: "",
      };
      const id = await sha256Hex(
        JSON.stringify([0, unsigned.pubkey, unsigned.created_at, unsigned.kind, unsigned.tags, unsigned.content]),
      );
      let sig: string;
      try {
        sig = bytesToHex(this.nip42.signId(hexToBytes(id)));
      } catch {
        return;
      }
      c.pendingOk.set(id, (ok) => {
        c.pendingOk.delete(id);
        if (ok) this.onAuthed(c);
      });
      if (c.ws === ws) ws.send(JSON.stringify(["AUTH", { ...unsigned, id, sig }]));
      return;
    }
    if (challenge && typeof challenge === "object" && this.devAuth) {
      const ch = challenge as { challenge?: unknown };
      if (typeof ch.challenge !== "string") return;
      const created_at = Math.floor(this.now() / 1000);
      const pubkey = this.devAuth.messagingPublicKeyHex.slice(0, 64).toLowerCase();
      const msg = new TextEncoder().encode(
        `veto/v1/relay-auth|${JSON.stringify([0, pubkey, ch.challenge, created_at])}`,
      );
      const sig = bytesToHex(this.devAuth.sign(msg));
      if (c.ws === ws) ws.send(JSON.stringify(["AUTH", { pubkey, challenge: ch.challenge, created_at, sig }]));
    }
  }

  private onAuthed(c: Conn): void {
    c.authed = true;
    c.authRequired = false;
    // Anything refused before authentication is retried now.
    this.sendInboxReqs(c);
    const q = c.queue.splice(0, c.queue.length);
    for (const f of q) c.ws?.send(f);
  }

  private enqueue(c: Conn, frame: string): void {
    if (c.queue.includes(frame)) return;
    c.queue.push(frame);
    if (c.queue.length > MAX_QUEUED_FRAMES) c.queue.splice(0, c.queue.length - MAX_QUEUED_FRAMES);
  }

  private flushSince(force: boolean): void {
    if (!this.sinceStore || !this.sinceDirty) return;
    const t = this.now();
    if (!force && t - this.lastSinceSave < 10_000) return;
    this.lastSinceSave = t;
    this.sinceDirty = false;
    try {
      this.sinceStore.save({ ...this.since });
    } catch {
      /* storage unavailable — catch-up falls back to the 30-day window */
    }
  }
}
